// ABOUTME: Reads the `appBar` system setting and resolves the layout shape for the current user,
// ABOUTME: preferring a role shape when exactly one of them matches.

import { useAuth } from '@src/hooks';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext, useMemo } from 'react';

import { AppBarSettings, AppBarShape } from '../types';

/**
 * Resolves the shape that applies to the current user:
 * exactly one matching role shape wins; none or several fall back to `default`.
 *
 * Returns `null` when the setting is absent, malformed or resolves to no shape, which makes the
 * layout resolver return the built-in bar.
 */
export function useAppBarShape(): AppBarShape | null {
    const { appBar } = useContext(SettingsContext);
    const { hasUserRole } = useAuth();

    return useMemo(() => {
        const config = appBar as AppBarSettings | null | undefined;
        if (!config || typeof config !== 'object') {
            return null;
        }

        const matches = Array.isArray(config.roleShapes)
            ? config.roleShapes.filter(entry => !!entry?.role && hasUserRole(entry.role))
            : [];

        if (matches.length === 1) {
            return matches[0].shape ?? null;
        }

        if (matches.length > 1 && !import.meta.env.PROD) {
            // Several role shapes match, so none of them can be preferred: without this the
            // fallback to `default` is indistinguishable from a missing role shape.
            // eslint-disable-next-line no-console
            console.warn(
                `appBar: ${matches.length} roleShapes match the current user (${matches
                    .map(entry => entry.role)
                    .join(', ')}); falling back to the default shape.`,
            );
        }

        return config.default ?? null;
    }, [appBar, hasUserRole]);
}
